import { useState } from 'react';
import { CoinsIcon, TicketIcon, UserXIcon } from 'lucide-react';
import {
  adminTicketGrantRequestSchema,
  adminUserBlockRequestSchema,
  adminUserPointsRequestSchema,
  type AdminUserSummary,
} from '@qaytim/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/date-picker';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { ErrorNotice, SuccessNotice } from '@/components/states';
import { useAdminForm } from '@/lib/admin-form';
import { useAdjustPoints, useBlockUser, useGrantTicket } from '@/lib/admin-queries';
import { strings } from '@/i18n/strings';

/**
 * Foydalanuvchi kartochkasidagi qo'lda amallar.
 *
 * Uchtasi ham modal ichida va uchtasida ham sabab MAJBURIY — bu shu yerdagi yagona umumiy
 * qoida va u tasodifiy emas: har bir amal boshqa odamning hisobiga aralashadi va uning
 * yagona izohi audit jurnalidagi qator bo'ladi. Sxema `@qaytim/shared` dan olinadi, ya'ni
 * forma va server BIR XIL qoidani tekshiradi — ilgari forma o'zicha tekshirar (yoki
 * tekshirmas) va server qoidani o'zgartirganda buni bilmasdi.
 *
 * Bu blok faqat `admin` roliga ko'rinadi (`visible`): operator uchun tugmalarni ko'rsatib,
 * keyin 403 bilan rad etish — ishlamaydigan tugma taklif qilish demakdir.
 */
export function UserActions({ user, visible }: { user: AdminUserSummary; visible: boolean }) {
  if (!visible) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{strings.userActions.heading}</CardTitle>
        <CardDescription>{strings.userActions.subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <BlockAction user={user} />
        <PointsAction user={user} />
        <TicketAction user={user} />
      </CardContent>
    </Card>
  );
}

function BlockAction({ user }: { user: AdminUserSummary }) {
  const [open, setOpen] = useState(false);
  const mutation = useBlockUser(user.id);
  const form = useAdminForm({
    schema: adminUserBlockRequestSchema,
    defaultValues: { isBlocked: !user.isBlocked, reason: '' },
  });

  const submit = form.handleSubmit((values) => {
    mutation.mutate(
      // `isBlocked` formadan emas, HOZIRGI holatdan hisoblanadi: oyna ochiq turganda boshqa
      // xodim holatni o'zgartirgan bo'lishi mumkin, va eski qiymatni yuborish uni jimgina
      // qaytarib qo'yardi. Server bir xil holatni rad etadi, ya'ni operator xabar oladi.
      { isBlocked: !user.isBlocked, reason: values.reason },
      { onSuccess: () => setOpen(false), onError: form.applyServerError },
    );
  });

  const blocking = !user.isBlocked;

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset({ isBlocked: !user.isBlocked, reason: '' });
      }}
      trigger={
        <Button variant={blocking ? 'destructive' : 'outline'} size="sm">
          <UserXIcon aria-hidden="true" />
          {blocking ? strings.userActions.block : strings.userActions.unblock}
        </Button>
      }
      title={blocking ? strings.userActions.blockTitle : strings.userActions.unblockTitle}
      description={blocking ? strings.userActions.blockHint : undefined}
      submitLabel={strings.userActions.submit}
      submitting={mutation.isPending}
      onSubmit={submit}
      error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
    >
      <Field
        label={strings.userActions.reason}
        hint={strings.userActions.reasonHint}
        error={form.formState.errors.reason?.message}
      >
        <Input {...form.register('reason')} />
      </Field>
    </FormDialog>
  );
}

function PointsAction({ user }: { user: AdminUserSummary }) {
  const [open, setOpen] = useState(false);
  const mutation = useAdjustPoints(user.id);
  const form = useAdminForm({
    schema: adminUserPointsRequestSchema,
    defaultValues: { amount: 0, reason: '' },
  });

  const submit = form.handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        setOpen(false);
        form.reset({ amount: 0, reason: '' });
      },
      onError: form.applyServerError,
    });
  });

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button variant="outline" size="sm">
            <CoinsIcon aria-hidden="true" />
            {strings.userActions.points}
          </Button>
        }
        title={strings.userActions.pointsTitle}
        submitLabel={strings.userActions.submit}
        submitting={mutation.isPending}
        onSubmit={submit}
        error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
      >
        <Field
          label={strings.userActions.pointsAmount}
          hint={strings.userActions.pointsAmountHint}
          error={form.formState.errors.amount?.message}
        >
          {/*
            `valueAsNumber` — aks holda `<input type="number">` satr qaytaradi va zod ning
            `z.number()` i uni butunlay boshqa xato bilan rad etardi ("expected number,
            received string"), ya'ni operator o'zi tushunmaydigan jumla ko'rardi.
          */}
          <Input type="number" {...form.register('amount', { valueAsNumber: true })} />
        </Field>

        <Field
          label={strings.userActions.reason}
          hint={strings.userActions.reasonHint}
          error={form.formState.errors.reason?.message}
        >
          <Input {...form.register('reason')} />
        </Field>
      </FormDialog>

      {/* Tasdiq oynadan TASHQARIDA: oyna yopilgach ichidagi xabar ko'rinmasdan yo'q bo'lardi. */}
      {mutation.isSuccess && !open ? (
        <SuccessNotice>
          {strings.userActions.pointsDone}: {mutation.data.user.currentBalance}
        </SuccessNotice>
      ) : null}
    </>
  );
}

function TicketAction({ user }: { user: AdminUserSummary }) {
  const [open, setOpen] = useState(false);
  const mutation = useGrantTicket(user.id);
  const form = useAdminForm({
    schema: adminTicketGrantRequestSchema,
    defaultValues: { acquiredDate: undefined, reason: '' },
  });

  const submit = form.handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        setOpen(false);
        form.reset({ acquiredDate: undefined, reason: '' });
      },
      onError: form.applyServerError,
    });
  });

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button variant="outline" size="sm">
            <TicketIcon aria-hidden="true" />
            {strings.userActions.ticket}
          </Button>
        }
        title={strings.userActions.ticketTitle}
        description={strings.userActions.ticketHint}
        submitLabel={strings.userActions.submit}
        submitting={mutation.isPending}
        onSubmit={submit}
        error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : undefined}
      >
        <Field
          label={strings.userActions.ticketDate}
          error={form.formState.errors.acquiredDate?.message}
        >
          <DatePicker
            name="acquiredDate"
            ariaLabel={strings.userActions.ticketDate}
            value={form.watch('acquiredDate') ?? ''}
            onChange={(value) =>
              form.setValue('acquiredDate', value === '' ? undefined : value, {
                shouldValidate: true,
              })
            }
          />
        </Field>

        <Field
          label={strings.userActions.reason}
          hint={strings.userActions.reasonHint}
          error={form.formState.errors.reason?.message}
        >
          <Input {...form.register('reason')} />
        </Field>
      </FormDialog>

      {mutation.isSuccess && !open ? (
        <SuccessNotice>
          {strings.userActions.ticketDone}: {mutation.data.code}
        </SuccessNotice>
      ) : null}
    </>
  );
}
